// Utility functions for working with assessor observations

/**
 * Groups observations by source ID
 * @param {Array} observations - Array of observation objects
 * @returns {Object} Object with sourceId as key and array of observations as value
 */
export const groupObservationsBySourceId = (observations) => {
  const grouped = {};
  
  if (!observations || !Array.isArray(observations)) {
    return grouped;
  }
  
  observations.forEach(observation => {
    const sourceId = observation.sourceid;
    if (sourceId && sourceId !== "0") {
      if (!grouped[sourceId]) {
        grouped[sourceId] = [];
      }
      grouped[sourceId].push(observation);
    }
  });
  
  return grouped;
};

/**
 * Gets all assessor names who have put observations for a specific source ID
 * @param {Array} observations - Array of observation objects
 * @param {string} sourceId - The source ID to filter by
 * @returns {Array} Array of unique assessor names for the given source ID
 */
export const getAssessorNamesForSourceId = (observations, sourceId) => {
  if (!observations || !Array.isArray(observations) || !sourceId) {
    return [];
  }
  
  const sourceObservations = observations.filter(obs => obs.sourceid === sourceId);
  const assessorNames = [...new Set(sourceObservations
    .filter(obs => obs.Name)
    .map(obs => obs.Name))];
  
  return assessorNames;
};

/**
 * Gets detailed assessor information for a specific source ID
 * @param {Array} observations - Array of observation objects
 * @param {string} sourceId - The source ID to filter by
 * @returns {Array} Array of assessor objects with detailed information
 */
export const getAssessorDetailsForSourceId = (observations, sourceId) => {
  if (!observations || !Array.isArray(observations) || !sourceId) {
    return [];
  }
  
  const sourceObservations = observations.filter(obs => obs.sourceid === sourceId);
  const assessorMap = new Map();
  
  sourceObservations.forEach(obs => {
    if (obs.Name && !assessorMap.has(obs.Name)) {
      assessorMap.set(obs.Name, {
        name: obs.Name,
        sectionName: obs.SectionName,
        subSectionName: obs.SubsectionName,
        status: obs.InformationStatus,
        insertDate: obs.Insertdate,
        clauseNo: obs.ClauseNo,
        observation: obs.Observation,
        informationProvided: obs.InformationProvided,
        assessorId: obs.Insertedby
      });
    }
  });
  
  return Array.from(assessorMap.values());
};

/**
 * Gets all source IDs that have observations from multiple assessors
 * @param {Array} observations - Array of observation objects
 * @returns {Array} Array of objects containing source ID and assessor count
 */
export const getSourceIdsWithMultipleAssessors = (observations) => {
  if (!observations || !Array.isArray(observations)) {
    return [];
  }
  
  const grouped = groupObservationsBySourceId(observations);
  
  return Object.entries(grouped)
    .map(([sourceId, obs]) => {
      const uniqueAssessors = [...new Set(obs
        .filter(o => o.Name)
        .map(o => o.Name))];
      
      return {
        sourceId,
        assessorCount: uniqueAssessors.length,
        assessors: uniqueAssessors,
        observationCount: obs.length
      };
    })
    .filter(item => item.assessorCount > 1)
    .sort((a, b) => b.assessorCount - a.assessorCount);
};

/**
 * Gets a summary of all assessors and their observation counts
 * @param {Array} observations - Array of observation objects
 * @returns {Object} Object with assessor names as keys and their stats as values
 */
export const getAssessorSummary = (observations) => {
  if (!observations || !Array.isArray(observations)) {
    return {};
  }
  
  const summary = {};
  
  observations.forEach(obs => {
    if (obs.Name) {
      if (!summary[obs.Name]) {
        summary[obs.Name] = {
          name: obs.Name,
          totalObservations: 0,
          sourceIds: new Set(),
          sections: new Set(),
          adequateCount: 0,
          inadequateCount: 0
        };
      }
      
      summary[obs.Name].totalObservations++;
      if (obs.sourceid && obs.sourceid !== "0") {
        summary[obs.Name].sourceIds.add(obs.sourceid);
      }
      if (obs.SectionName) {
        summary[obs.Name].sections.add(obs.SectionName);
      }
      
      if (obs.InformationStatus === 'Adequate') {
        summary[obs.Name].adequateCount++;
      } else if (obs.InformationStatus === 'Inadequate') {
        summary[obs.Name].inadequateCount++;
      }
    }
  });
  
  // Convert sets to arrays for easier use
  Object.values(summary).forEach(assessor => {
    assessor.sourceIds = Array.from(assessor.sourceIds);
    assessor.sections = Array.from(assessor.sections);
    assessor.uniqueSourceCount = assessor.sourceIds.length;
    assessor.uniqueSectionCount = assessor.sections.length;
  });
  
  return summary;
};

/**
 * Formats assessor names for display
 * @param {Array} assessorNames - Array of assessor names
 * @param {string} separator - Separator to use between names (default: ', ')
 * @returns {string} Formatted string of assessor names
 */
export const formatAssessorNames = (assessorNames, separator = ', ') => {
  if (!assessorNames || !Array.isArray(assessorNames) || assessorNames.length === 0) {
    return 'No assessors';
  }
  
  if (assessorNames.length === 1) {
    return assessorNames[0];
  }
  
  if (assessorNames.length === 2) {
    return assessorNames.join(' and ');
  }
  
  return assessorNames.slice(0, -1).join(separator) + ' and ' + assessorNames[assessorNames.length - 1];
};